import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Row,
  Button
} from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import TimeScheduleService from '../../TimeSchedule/service';
import ExtensionService from '../../Extension/service';
import ReactLoader from 'components/Loader';
import useRingGroups from 'hooks/useRingGroups';
import useCostCenters from 'hooks/useCostCenters';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';
import useQueues from 'hooks/useQueues';
import useAudios from 'hooks/useAudios';
import { strategyOptions } from 'views/Queue/form/assets';
import { strategyTitle } from 'views/Queue/form/assets';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

const requiredSelectSchema = (label) =>
  yup
    .object()
    .nullable()
    .shape({
      label: yup.string().required(),
      value: yup.string().required()
    })
    .test(
      'is-required',
      `${label} é obrigatório`,
      (value) =>
        value &&
        value.value !== null &&
        value.value !== undefined &&
        value.value !== ''
    );

const optionalSelectSchema = (label) =>
  yup
    .object()
    .nullable()
    .shape({
      label: yup.string().nullable(),
      value: yup.string().nullable()
    })
    .test('is-valid-option', `${label} deve ser selecionado`, (value) => {
      if (value === null || value === undefined) return true;
      return (
        typeof value === 'object' &&
        (value.value === null ||
          value.value === undefined ||
          value.value === '' ||
          !isNaN(Number(value.value)))
      );
    });

const overflowTypeOptions = [
  { label: 'Fila', value: 'QUEUE' },
  { label: 'Ramal', value: 'EXTENSION' },
  { label: 'Desligar', value: 'HANGUP' },
  { label: 'Grupo de Toque', value: 'RING_GROUP' }
];

const ringGroupFormSchema = yup.object().shape({
  name: yup
    .string()
    .required('Nome é obrigatório')
    .matches(
      /^[a-zA-Z0-9]+$/,
      'O nome não pode conter caracteres especiais ou espaços'
    ),
  alter_cost_center: yup
    .boolean()
    .required('Alterar centro de custo é obrigatório'),
  ring_strategy: yup
    .object()
    .shape({
      label: yup.string().required(),
      value: yup
        .string()
        .oneOf(
          [
            'FEWESTCALLS',
            'LEASTRECENT',
            'LINEAR',
            'RANDOM',
            'RINGALL',
            'RRMEMORY'
          ],
          'Estratégia de toque inválida'
        )
        .required('Estratégia de toque é obrigatória')
    })
    .required('Estratégia de toque é obrigatória')
    .typeError('Estratégia de toque deve ser selecionada'),
  ring_time: yup
    .number()
    .integer('Tempo de toque deve ser um número inteiro')
    .min(1, 'Tempo de toque deve ser maior que 0')
    .max(300, 'Tempo de toque é no máximo 300 segundos')
    .required('Tempo de toque é obrigatório')
    .typeError('Tempo de toque deve ser um número'),
  enabled: yup
    .boolean()
    .required('Status de habilitado é obrigatório')
    .default(true),
  extensionsid: yup.array().of(
    yup.object().shape({
      label: yup.string().required(),
      value: yup.string().required()
    })
  ),
  cost_center_id: requiredSelectSchema('Setor'),
  music_id: optionalSelectSchema('Música em Espera'),
  periodic_announce_frequency: yup
    .number()
    .when('periodic_announce_id', {
      is: (val) => !!val,
      then: (schema) =>
        schema
          .required(
            'A frequência de anúncio periódico deve ser informada quando um áudio for selecionado'
          )
          .integer('Frequência do anúncio periódico deve ser um número inteiro')
          .min(1, 'Frequência do anúncio periódico deve ser no mínimo 1')
    })
    .nullable(),
  periodic_announce_id: optionalSelectSchema('Áudio do Anúncio Periódico'),
  is_personalized: yup
    .boolean()
    .required('Personalizado é obrigatório')
    .default(false),
  schedule_time_id: requiredSelectSchema('Horário de funcionamento'),
  outside_hours_enabled: yup.boolean().when('schedule_time_id', {
    is: (val) => val && val.value,
    then: () =>
      yup
        .boolean()
        .required('Habilitar transbordo fora do horário é obrigatório')
        .default(false),
    otherwise: () => yup.boolean().default(false)
  }),
  outside_hours_type: yup
    .object()
    .nullable()
    .when('outside_hours_enabled', {
      is: true,
      then: () => requiredSelectSchema('Tipo de transbordo fora do horário'),
      otherwise: () =>
        optionalSelectSchema('Tipo de transbordo fora do horário')
    }),
  outside_hours_destination_id: optionalSelectSchema(
    'Destino do transbordo fora do horário'
  ).when('outside_hours_type', {
    is: (type) =>
      type?.value === 'QUEUE' ||
      type?.value === 'EXTENSION' ||
      type?.value === 'RING_GROUP',
    then: () => requiredSelectSchema('Destino do transbordo fora do horário')
  }),
  overflow_enabled: yup
    .boolean()
    .required('Transbordo ativado é obrigatório')
    .default(false),
  overflow_type: yup
    .object()
    .nullable()
    .when('overflow_enabled', {
      is: true,
      then: () =>
        yup
          .object()
          .nullable()
          .shape({
            label: yup.string().required(),
            value: yup
              .string()
              .oneOf(
                ['QUEUE', 'EXTENSION', 'HANGUP', 'RING_GROUP'],
                'Tipo de transbordo inválido'
              )
              .required('Tipo de transbordo é obrigatório')
          })
          .required('Tipo de transbordo é obrigatório')
          .typeError('Tipo de transbordo deve ser selecionado')
    }),
  overflow_destination_id: optionalSelectSchema('Destino do transbordo').when(
    'overflow_type',
    {
      is: (type) =>
        type?.value === 'QUEUE' ||
        type?.value === 'EXTENSION' ||
        type?.value === 'RING_GROUP',
      then: () => requiredSelectSchema('Destino do transbordo')
    }
  ),
  timeout_enabled: yup
    .boolean()
    .required('Tempo limite ativado é obrigatório')
    .default(false),
  timeout_type: yup
    .object()
    .nullable()
    .when('timeout_enabled', {
      is: true,
      then: () =>
        yup
          .object()
          .nullable()
          .shape({
            label: yup.string().required(),
            value: yup
              .string()
              .oneOf(
                ['QUEUE', 'EXTENSION', 'HANGUP', 'RING_GROUP'],
                'Tipo de tempo limite inválido'
              )
              .required('Tipo de tempo limite é obrigatório')
          })
          .required('Tipo de tempo limite é obrigatório')
          .typeError('Tipo de tempo limite deve ser selecionado')
    }),
  timeout_destination_id: optionalSelectSchema('Destino do tempo limite').when(
    'timeout_type',
    {
      is: (type) =>
        type?.value === 'QUEUE' ||
        type?.value === 'EXTENSION' ||
        type?.value === 'RING_GROUP',
      then: () => requiredSelectSchema('Destino do tempo limite')
    }
  ),
  timeout_seconds: yup
    .number()
    .integer()
    .nullable()
    .when('timeout_enabled', {
      is: true,
      then: () =>
        yup
          .number()
          .integer()
          .min(1, 'Tempo limite em segundos deve ser no mínimo 1')
          .required('Tempo limite em segundos é obrigatório')
          .typeError('Tempo limite em segundos deve ser um número')
    }),
  wrapuptime: yup
    .number()
    .integer('Tempo entre chamadas deve ser um número inteiro')
    .min(0, 'Tempo entre chamadas deve ser maior ou igual a 0')
    .required('Tempo entre chamadas é obrigatório')
    .typeError('Tempo entre chamadas deve ser um número'),
  announcePosition: yup
    .boolean()
    .required('Posição de anúncio é obrigatória')
    .default(false),
  announceFrequency: yup
    .number()
    .integer('Frequência do anúncio deve ser um número inteiro')
    .nullable()
    .when('announcePosition', {
      is: true,
      then: () =>
        yup
          .number()
          .integer()
          .min(1, 'Frequência do anúncio deve ser no mínimo 1')
          .required('Frequência do anúncio é obrigatória')
          .typeError('Frequência do anúncio deve ser um número')
    }),
  record_calls: yup
    .boolean()
    .required('Gravar chamadas é obrigatório')
    .default(false)
});

const RingGroupForm = ({ id, isUpdate }) => {
  const history = useHistory();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [operatingHours, setOperatingHours] = useState([]);
  const [extensions, setExtensions] = useState([]);

  const {
    fetchRingGroupById,
    createRingGroup,
    updateRingGroup,
    options: ringGroupRawOptions
  } = useRingGroups();

  const ringGroupOptions = useMemo(() => {
    return ringGroupRawOptions.filter((opt) => opt.value != id);
  }, [ringGroupRawOptions, id]);

  const useCostCenterProps = useMemo(
    () => ({ login: localStorage.getItem('login') }),
    [localStorage.getItem('login')]
  );

  const { response: costCenters, isLoading: isLoadingCostCenters } =
    useCostCenters(useCostCenterProps);
  const { options: queueOptions } = useQueues({ types: ['is_telephony'] });

  const {
    response: allAudios,
    isLoading: isLoadingAudios,
    groupedByType
  } = useAudios();

  const { reset, control, handleSubmit, formState, watch, setValue } = useForm({
    resolver: yupResolver(ringGroupFormSchema),
    context: { isUpdate },
    defaultValues: {
      alter_cost_center: false,
      periodic_announce_id: null
    }
  });
  const { errors, isSubmitting } = formState;

  const isOverflowEnabled = watch('overflow_enabled');
  const isTimeoutEnabled = watch('timeout_enabled');
  const isOutsideHoursEnabled = watch('outside_hours_enabled');
  const overflowType = watch('overflow_type');
  const timeoutType = watch('timeout_type');
  const outsideHoursType = watch('outside_hours_type');
  const announcePosition = watch('announcePosition');

  const costCenterOptions = useMemo(
    () =>
      costCenters.map((cc) => ({
        label: cc.name,
        value: String(cc.id)
      })),
    [costCenters]
  );

  const musicHoldOptions = useMemo(
    () =>
      (groupedByType.musicHold || []).map((audio) => ({
        label: audio.name,
        value: String(audio.id)
      })),
    [groupedByType.musicHold]
  );

  const soundOptions = useMemo(
    () =>
      (groupedByType.sound || []).map((audio) => ({
        label: audio.name,
        value: String(audio.id)
      })),
    [groupedByType.sound]
  );

  const operatingHoursOptions = useMemo(
    () =>
      operatingHours.map((hour) => ({
        label: hour.name,
        value: String(hour.id)
      })),
    [operatingHours]
  );

  const extensionOptions = useMemo(
    () =>
      extensions.map((ext) => ({
        label: ext.extension_number,
        value: String(ext.extension_number)
      })),
    [extensions]
  );

  const overflowDestinationOptions = useMemo(() => {
    if (overflowType?.value === 'QUEUE') {
      return queueOptions ?? [];
    } else if (overflowType?.value === 'EXTENSION') {
      return extensionOptions ?? [];
    } else if (overflowType?.value === 'RING_GROUP') {
      return ringGroupOptions ?? [];
    } else if (overflowType?.value === 'HANGUP') {
      return (groupedByType.sound || []).map((audio) => ({
        label: audio.name,
        value: String(audio.id)
      }));
    }
    return [];
  }, [
    overflowType,
    queueOptions,
    extensionOptions,
    ringGroupOptions,
    groupedByType.sound
  ]);

  const timeoutDestinationOptions = useMemo(() => {
    if (timeoutType?.value === 'QUEUE') {
      return queueOptions ?? [];
    } else if (timeoutType?.value === 'EXTENSION') {
      return extensionOptions ?? [];
    } else if (timeoutType?.value === 'RING_GROUP') {
      return ringGroupOptions ?? [];
    } else if (timeoutType?.value === 'HANGUP') {
      return (groupedByType.sound || []).map((audio) => ({
        label: audio.name,
        value: String(audio.id)
      }));
    }
    return [];
  }, [
    timeoutType,
    queueOptions,
    extensionOptions,
    ringGroupOptions,
    groupedByType.sound
  ]);

  const outsideHoursDestinationOptions = useMemo(() => {
    if (outsideHoursType?.value === 'QUEUE') {
      return queueOptions ?? [];
    } else if (outsideHoursType?.value === 'EXTENSION') {
      return extensionOptions ?? [];
    } else if (outsideHoursType?.value === 'RING_GROUP') {
      return ringGroupOptions ?? [];
    } else if (outsideHoursType?.value === 'HANGUP') {
      return (groupedByType.sound || []).map((audio) => ({
        label: audio.name,
        value: String(audio.id)
      }));
    }
    return [];
  }, [
    outsideHoursType,
    queueOptions,
    extensionOptions,
    ringGroupOptions,
    groupedByType.sound
  ]);

  useEffect(() => {
    if (!isOverflowEnabled) {
      setValue('overflow_destination_id', null);
      setValue('overflow_type', null);
    }
  }, [isOverflowEnabled, setValue]);

  useEffect(() => {
    const currentDestination = watch('overflow_destination_id');
    if (currentDestination) {
      const isValid = overflowDestinationOptions.some(
        (option) => String(option.value) === String(currentDestination.value)
      );
      if (!isValid) {
        setValue('overflow_destination_id', null);
      }
    }
  }, [overflowType, setValue, watch, overflowDestinationOptions]);

  useEffect(() => {
    if (!isTimeoutEnabled) {
      setValue('timeout_destination_id', null);
      setValue('timeout_type', null);
      setValue('timeout_seconds', null);
    }
  }, [isTimeoutEnabled, setValue]);

  useEffect(() => {
    const currentDestination = watch('timeout_destination_id');
    if (currentDestination) {
      const isValid = timeoutDestinationOptions.some(
        (option) => String(option.value) === String(currentDestination.value)
      );
      if (!isValid) {
        setValue('timeout_destination_id', null);
      }
    }
  }, [timeoutType, setValue, watch, timeoutDestinationOptions]);

  useEffect(() => {
    if (!announcePosition) {
      setValue('announceFrequency', null);
    }
  }, [announcePosition, setValue]);

  useEffect(() => {
    if (!isOutsideHoursEnabled) {
      setValue('outside_hours_destination_id', null);
      setValue('outside_hours_type', null);
    }
  }, [isOutsideHoursEnabled, setValue]);

  useEffect(() => {
    const currentDestination = watch('outside_hours_destination_id');
    if (currentDestination) {
      const isValid = outsideHoursDestinationOptions.some(
        (option) => String(option.value) === String(currentDestination.value)
      );
      if (!isValid) {
        setValue('outside_hours_destination_id', null);
      }
    }
  }, [outsideHoursType, setValue, watch, outsideHoursDestinationOptions]);

  const loadFormData = useCallback(async () => {
    try {
      const [operatingHoursResponse, extensionsResponse] = await Promise.all([
        TimeScheduleService.getAll(),
        ExtensionService.getAll()
      ]);

      setOperatingHours(
        operatingHoursResponse.data.data || operatingHoursResponse.data
      );
      setExtensions(extensionsResponse.data.data || extensionsResponse.data);
    } catch (err) {
      console.error('Erro ao carregar dados do formulário:', err);
      toast.error('Erro ao carregar dados do formulário');
    }
  }, []);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);

  useEffect(() => {
    if (!isInitialLoading && !isLoadingCostCenters && !isLoadingAudios) {
      setIsInitialLoading(false);
    }
  }, [isInitialLoading, isLoadingCostCenters, isLoadingAudios]);

  const findOption = useCallback((value, options) => {
    if (value === null || value === undefined) {
      return null;
    }
    const searchValue = String(value);

    return options.find((opt) => String(opt.value) === searchValue) || null;
  }, []);

  const loadRingGroup = useCallback(
    async (ringGroupId) => {
      try {
        const ringGroup = await fetchRingGroupById(ringGroupId);

        if (ringGroup) {
          const initialOverflowDestOptions =
            ringGroup.overflow_type === 'QUEUE'
              ? queueOptions
              : ringGroup.overflow_type === 'EXTENSION'
              ? extensionOptions
              : ringGroup.overflow_type === 'RING_GROUP'
              ? ringGroupOptions
              : ringGroup.overflow_type === 'HANGUP'
              ? soundOptions
              : [];

          const initialTimeoutDestOptions =
            ringGroup.timeout_type === 'QUEUE'
              ? queueOptions
              : ringGroup.timeout_type === 'EXTENSION'
              ? extensionOptions
              : ringGroup.timeout_type === 'RING_GROUP'
              ? ringGroupOptions
              : ringGroup.timeout_type === 'HANGUP'
              ? soundOptions
              : [];

          const initialOutsideHoursDestOptions =
            ringGroup.outside_hours_type === 'QUEUE'
              ? queueOptions
              : ringGroup.outside_hours_type === 'EXTENSION'
              ? extensionOptions
              : ringGroup.outside_hours_type === 'RING_GROUP'
              ? ringGroupOptions
              : ringGroup.outside_hours_type === 'HANGUP'
              ? soundOptions
              : [];

          reset({
            name: ringGroup.name || '',
            alter_cost_center: ringGroup.alter_cost_center ?? false,
            ring_strategy: findOption(ringGroup.ring_strategy, strategyOptions),
            ring_time: ringGroup.ring_time || '',
            enabled: !!ringGroup.enabled ?? true,
            extensionsid:
              ringGroup.extensions
                ?.map((rgExt) =>
                  extensionOptions.find(
                    (opt) => opt.value === String(rgExt.extension_number)
                  )
                )
                .filter(Boolean) || [],
            cost_center_id: findOption(
              ringGroup.cost_center_id,
              costCenterOptions
            ),
            music_id: findOption(ringGroup.music_id, musicHoldOptions),
            periodic_announce_frequency:
              ringGroup.periodic_announce_frequency ?? null,
            periodic_announce_id: findOption(
              ringGroup.periodic_announce_id,
              soundOptions
            ),
            is_personalized: ringGroup.is_personalized || false,
            schedule_time_id: findOption(
              ringGroup.schedule_time_id,
              operatingHoursOptions
            ),
            outside_hours_enabled: ringGroup.outside_hours_enabled || false,
            outside_hours_type: findOption(
              ringGroup.outside_hours_type,
              overflowTypeOptions
            ),
            outside_hours_destination_id: findOption(
              ringGroup.outside_hours_destination_id,
              initialOutsideHoursDestOptions
            ),

            overflow_enabled: ringGroup.overflow_enabled || false,
            overflow_type: findOption(
              ringGroup.overflow_type,
              overflowTypeOptions
            ),
            overflow_destination_id: findOption(
              ringGroup.overflow_destination_id,
              initialOverflowDestOptions
            ),
            timeout_enabled: ringGroup.timeout_enabled || false,
            timeout_type: findOption(
              ringGroup.timeout_type,
              overflowTypeOptions
            ),
            timeout_destination_id: findOption(
              ringGroup.timeout_destination_id,
              initialTimeoutDestOptions
            ),
            timeout_seconds: ringGroup.timeout_seconds ?? null,
            wrapuptime: ringGroup.wrapuptime ?? 0,
            announcePosition: ringGroup.announcePosition || false,
            announceFrequency: ringGroup.announceFrequency ?? null,
            record_calls: ringGroup.record_calls ?? false
          });
        } else {
          toast.error(
            'Não foi possível carregar os detalhes do grupo de toque.'
          );
          history.push('/admin/ringgroups');
        }
      } catch (err) {
        console.error('Erro ao carregar grupo de toque:', err);
        toast.error('Erro ao carregar grupo de toque');
      }
    },
    [
      fetchRingGroupById,
      reset,
      history,
      costCenterOptions,
      musicHoldOptions,
      soundOptions,
      operatingHoursOptions,
      queueOptions,
      extensionOptions,
      findOption,
      strategyOptions,
      ringGroupOptions
    ]
  );

  useEffect(() => {
    if (
      isUpdate &&
      id &&
      costCenters.length &&
      allAudios.length &&
      operatingHours.length &&
      queueOptions.length &&
      extensions.length &&
      ringGroupRawOptions.length &&
      !isLoadingCostCenters &&
      !isLoadingAudios
    ) {
      loadRingGroup(id).finally(() => {
        setIsInitialLoading(false);
      });
    } else if (!isUpdate) {
      reset();
      setIsInitialLoading(false);
    }
  }, [
    id,
    isUpdate,
    loadRingGroup,
    costCenters.length,
    allAudios.length,
    operatingHours.length,
    queueOptions.length,
    extensions.length,
    isLoadingCostCenters,
    isLoadingAudios,
    reset,
    ringGroupRawOptions.length
  ]);

  const onSubmit = useCallback(
    async (data) => {
      try {
        const dirtyFieldNames = Object.keys(formState.dirtyFields);
        const isUpdatingExtensionsOnly =
          isUpdate &&
          dirtyFieldNames.length === 1 &&
          dirtyFieldNames[0] === 'extensionsid';

        const ringGroupData = {
          ...data,
          is_personalized: data.is_personalized || false,
          outside_hours_enabled: data.outside_hours_enabled || false,
          overflow_enabled: data.overflow_enabled || false,
          timeout_enabled: data.timeout_enabled || false,
          announcePosition: data.announcePosition || false,
          record_calls: data.record_calls || false,
          cost_center_id: data.cost_center_id
            ? Number(data.cost_center_id.value)
            : null,
          music_id: data.music_id?.value ? data.music_id?.value : null,
          periodic_announce_frequency: data.periodic_announce_frequency ?? 0,
          periodic_announce_id: data.periodic_announce_id
            ? Number(data.periodic_announce_id.value)
            : null,
          schedule_time_id: data.schedule_time_id
            ? Number(data.schedule_time_id.value)
            : null,
          outside_hours_type: data.outside_hours_type?.value || null,
          outside_hours_destination_id: data.outside_hours_destination_id
            ? String(data.outside_hours_destination_id.value)
            : null,
          overflow_destination_id: data.overflow_destination_id
            ? String(data.overflow_destination_id.value)
            : null,
          timeout_destination_id: data.timeout_destination_id
            ? String(data.timeout_destination_id.value)
            : null,
          ring_strategy: data.ring_strategy ? data.ring_strategy.value : null,
          overflow_type: data.overflow_type ? data.overflow_type.value : null,
          timeout_type: data.timeout_type ? data.timeout_type.value : null,
          ring_time: data.ring_time === '' ? null : data.ring_time,
          timeout_seconds:
            data.timeout_seconds === '' ? null : data.timeout_seconds,
          wrapuptime: data.wrapuptime === '' ? 0 : data.wrapuptime,
          extensionsid: data.extensionsid?.map?.((opt) => opt.value) || [],
          announceFrequency:
            data.announceFrequency === '' ? null : data.announceFrequency,
          // O campo `extension_number` agora obtém o valor do objeto selecionado
          extension_number: data.extension_number
            ? String(data.extension_number.value)
            : null
        };

        if (isUpdate) {
          await updateRingGroup(id, {
            ...ringGroupData,
            is_updating_extensions_only: isUpdatingExtensionsOnly
          });
        } else {
          await createRingGroup(ringGroupData);
        }

        reset();
        history.push('/admin/ringgroups');
      } catch (err) {
        console.error('Erro ao salvar grupo de toque:', err);
        const errorMessage =
          err.response?.data?.message || 'Erro ao salvar grupo de toque';
        toast.error(errorMessage, {
          autoClose: 3000,
          closeOnClick: true
        });
      }
    },
    [
      id,
      isUpdate,
      history,
      createRingGroup,
      updateRingGroup,
      reset,
      JSON.stringify(formState.dirtyFields)
    ]
  );

  const cancelAction = useCallback(() => {
    history.push('/admin/ringgroups');
  }, [history]);

  if (isInitialLoading || isLoadingCostCenters || isLoadingAudios) {
    return (
      <Container className="mt--6" fluid>
        <Row className="justify-content-md-center">
          <ReactLoader />
        </Row>
      </Container>
    );
  }

  return (
    <>
      <SimpleHeader
        name={isUpdate ? 'Editar Grupo de Toque' : 'Criar Grupo de Toque'}
        parentName="Telefonia"
        returnPath="/admin/ringgroups"
      />
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader>
                <h3>Grupos de toque</h3>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Row className="mb-3">
                    <Col>
                      <h3 className="mb-1">Principal</h3>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="text"
                          name="name"
                          control={control}
                          label="Nome do Grupo de Toque*"
                          placeholder="Grupo 1"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="select"
                          name="ring_strategy"
                          control={control}
                          label="Estratégia do Grupo de Toque*"
                          title={strategyTitle}
                          options={strategyOptions}
                          placeholder="Selecione uma estratégia"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="select"
                          name="cost_center_id"
                          control={control}
                          label="Setor do Grupo de Toque*"
                          options={costCenterOptions}
                          placeholder="Selecione um setor"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="select"
                          name="extensionsid"
                          control={control}
                          label="Ramais do Grupo de Toque*"
                          options={extensionOptions}
                          isMulti
                          closeMenuOnSelect={false}
                          placeholder="Selecione os ramais"
                          errors={errors}
                          disabled={
                            !verifyRoles(['RING_GROUPS_ADD_EXTENSIONS'])
                          }
                          title="É possível atribuir ramais por meio da tabela de listagem de grupos de toque"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <hr />

                  <Row className="mb-3 mt-3">
                    <Col>
                      <h3 className="mb-1">Configurações</h3>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="number"
                          name="ring_time"
                          control={control}
                          label="Tempo de Toque (segundos)*"
                          placeholder="Ex: 30"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="number"
                          name="wrapuptime"
                          control={control}
                          label="Tempo entre Chamadas (segundos)*"
                          placeholder="Ex: 0"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="select"
                          name="music_id"
                          control={control}
                          label="Música de Espera"
                          options={musicHoldOptions}
                          placeholder="Selecione uma Música"
                          isClearable
                          errors={errors}
                          noOptionsMessage={() =>
                            'Nenhuma música de espera disponível'
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup check>
                        <FlexControllerV2
                          type="checkbox"
                          name="record_calls"
                          control={control}
                          label="Gravar chamadas"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup check>
                        <FlexControllerV2
                          type="checkbox"
                          name="alter_cost_center"
                          control={control}
                          label="Alterar setor para o de atendimento"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup check>
                        <FlexControllerV2
                          type="checkbox"
                          name="is_personalized"
                          control={control}
                          label="Grupo de Toque Personalizado"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup check>
                        <FlexControllerV2
                          type="checkbox"
                          name="enabled"
                          control={control}
                          label="Habilitado"
                          errors={errors}
                          hidden
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <hr />

                  <Row className="mb-3 mt-3">
                    <Col>
                      <h3 className="mb-1">Programação de Horário</h3>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="select"
                          name="schedule_time_id"
                          control={control}
                          label="Horário de Funcionamento*"
                          options={operatingHoursOptions}
                          placeholder="Selecione o horário"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <hr />

                  <Row className="mb-3">
                    <Col>
                      <h3 className="mb-1">Facilidade</h3>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="select"
                          name="periodic_announce_id"
                          control={control}
                          label="Áudio do Anúncio Periódico"
                          options={soundOptions}
                          placeholder="Selecione um Áudio"
                          isClearable
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <FlexControllerV2
                          type="number"
                          name="periodic_announce_frequency"
                          control={control}
                          label="Frequência do Anúncio Periódico (segundos)"
                          placeholder="Ex: 60"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="6">
                      <FormGroup check>
                        <FlexControllerV2
                          type="checkbox"
                          name="announcePosition"
                          control={control}
                          label="Anunciar Posição da Fila"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                    {announcePosition && (
                      <Col md="6">
                        <FormGroup>
                          <FlexControllerV2
                            type="number"
                            name="announceFrequency"
                            control={control}
                            label="Frequência do Anúncio de Posição (segundos)"
                            placeholder="Ex: 30"
                            errors={errors}
                          />
                        </FormGroup>
                      </Col>
                    )}
                  </Row>

                  <hr />

                  <Row className="mb-3">
                    <Col>
                      <h3 className="mb-1">Transbordo</h3>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md="4">
                      <FormGroup check>
                        <FlexControllerV2
                          type="checkbox"
                          name="outside_hours_enabled"
                          control={control}
                          label="Transbordar caso não atenda"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  {isOutsideHoursEnabled && (
                    <Row className="mt-1">
                      <Col md="6">
                        <FormGroup>
                          <FlexControllerV2
                            type="select"
                            name="outside_hours_type"
                            control={control}
                            label="Tipo de transbordo caso não atenda*"
                            options={overflowTypeOptions}
                            placeholder="Selecione o tipo"
                            errors={errors}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <FlexControllerV2
                            type="select"
                            name="outside_hours_destination_id"
                            control={control}
                            label="Destino do transbordo caso não atenda*"
                            options={outsideHoursDestinationOptions}
                            placeholder="Selecione o destino"
                            errors={errors}
                            disabled={!outsideHoursType?.value}
                            isClearable={outsideHoursType?.value === 'HANGUP'}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col md="12">
                      <FormGroup check>
                        <FlexControllerV2
                          type="checkbox"
                          name="timeout_enabled"
                          control={control}
                          label="Transbordar com tempo limite"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {isTimeoutEnabled && (
                    <Row className="mt-1">
                      <Col md="4">
                        <FormGroup>
                          <FlexControllerV2
                            type="select"
                            name="timeout_type"
                            control={control}
                            label="Tipo de Transbordo por Tempo*"
                            options={overflowTypeOptions}
                            placeholder="Selecione o tipo"
                            errors={errors}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <FlexControllerV2
                            type="select"
                            name="timeout_destination_id"
                            control={control}
                            label="Destino do Transbordo por Tempo*"
                            options={timeoutDestinationOptions}
                            placeholder="Selecione o destino"
                            errors={errors}
                            disabled={!timeoutType?.value}
                            isClearable={timeoutType?.value === 'HANGUP'}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <FlexControllerV2
                            type="number"
                            name="timeout_seconds"
                            control={control}
                            label="Tempo Limite em Segundos*"
                            placeholder="30"
                            errors={errors}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                  <Row className="mt-3">
                    <Col md="4">
                      <FormGroup check>
                        <FlexControllerV2
                          type="checkbox"
                          name="overflow_enabled"
                          control={control}
                          label="Transbordar caso ocupado"
                          errors={errors}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  {isOverflowEnabled && (
                    <Row className="mt-1">
                      <Col md="6">
                        <FormGroup>
                          <FlexControllerV2
                            type="select"
                            name="overflow_type"
                            control={control}
                            label="Tipo de Transbordo por Ocupação*"
                            options={overflowTypeOptions}
                            placeholder="Selecione o tipo"
                            errors={errors}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <FlexControllerV2
                            type="select"
                            name="overflow_destination_id"
                            control={control}
                            label="Destino do Transbordo por Ocupação*"
                            options={overflowDestinationOptions}
                            placeholder="Selecione o destino"
                            errors={errors}
                            disabled={!overflowType?.value}
                            isClearable={overflowType?.value === 'HANGUP'}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col className="text-right">
                      <Button
                        color="secondary"
                        type="button"
                        onClick={cancelAction}
                        className="mr-2"
                      >
                        Cancelar
                      </Button>
                      <Button
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default RingGroupForm;
